import { useGetFormattedDate } from '@app/src/components/DateDisplay'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { toError } from '@app/src/errors/toError'
import sentryService from '@app/src/services/SentryService'
import { getCurrentTimestamp } from '@app/src/utils'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import { useState } from 'react'
import { useIntl } from 'react-intl'

const styles = {
  logo: {
    width: 10,
    height: 10,
    marginBottom: 6,
    marginLeft: 3,
    marginTop: 5,
  },
  page: {
    padding: 12.7,
    pageBreakGap: 5,
  },
  headerRow: {
    fontSize: 8,
    fontWeight: 'normal',
    fontFamily: 'helvetica',
    lineHeight: 3.5,
  },
} as const

const HIDDEN_SELECTORS = ['[data-pdf-hide]']

const logo = '/img/brand/worldfavor_Logo_Black.png'

const IMAGE_COMPRESSION = 'FAST'

const PDF_BLOCK_SELECTOR = '[data-pdf-block]'
const PDF_TITLE_ID = 'pdf-export-title'

const prepareForExport = (container: HTMLElement, pdfTitle: string): void => {
  HIDDEN_SELECTORS.forEach(selector => {
    container.querySelectorAll(selector).forEach(el => {
      if (el instanceof HTMLElement) {
        el.style.display = 'none'
      }
    })
  })

  container.querySelectorAll('.collapsed-content').forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.maxHeight = 'none'
      el.style.overflow = 'visible'
    }
  })

  // Hide MUI Dividers during export
  container.querySelectorAll('.MuiDivider-root').forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.display = 'none'
    }
  })

  // Remove grey backgrounds from answer boxes
  container.querySelectorAll('[data-pdf-white]').forEach(el => {
    if (el instanceof HTMLElement) {
      el.style.backgroundColor = 'transparent'
    }
  })

  // Replace MUI Chips with plain text spans that html2canvas can render
  container.querySelectorAll('.MuiChip-root').forEach(el => {
    if (el instanceof HTMLElement) {
      const label = el.querySelector('.MuiChip-label')
      const text = label?.textContent ?? el.textContent ?? ''
      const placeholder = document.createElement('span')
      placeholder.textContent = text
      placeholder.style.fontSize = '13px'
      placeholder.style.color = '#616161'
      placeholder.style.padding = '4px 8px'
      placeholder.style.border = '1px solid #bdbdbd'
      placeholder.style.borderRadius = '16px'
      placeholder.style.display = 'inline-block'
      placeholder.style.lineHeight = '1.4'
      el.parentNode?.insertBefore(placeholder, el)
      el.style.display = 'none'
    }
  })

  // Insert a PDF-only title element after the first divider
  const titleEl = document.createElement('div')
  titleEl.id = PDF_TITLE_ID
  titleEl.style.fontSize = '36px'
  titleEl.style.fontWeight = 'bold'
  titleEl.style.fontFamily = '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif'
  titleEl.style.marginBottom = '16px'
  titleEl.style.color = '#212121'
  titleEl.textContent = pdfTitle

  // Find the first visible (non-hidden) child to insert the title before
  const firstVisibleChild = Array.from(container.children).find(
    child => child instanceof HTMLElement && child.style.display !== 'none',
  )
  container.insertBefore(titleEl, firstVisibleChild ?? container.firstChild)
}

const imgToBase64 = (url: string) => {
  return new Promise<string>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.onload = function () {
      const reader = new FileReader()
      reader.onloadend = function () {
        resolve(reader.result as string)
      }
      reader.readAsDataURL(xhr.response)
    }
    xhr.onerror = reject
    xhr.open('GET', url)
    xhr.responseType = 'blob'
    xhr.send()
  })
}

const useResponseToPDF = (
  elementRef: React.RefObject<HTMLDivElement>,
  requestTitle: string | undefined,
  responderName: string | undefined,
  periodName: string | undefined,
  submittedAt: string | undefined,
) => {
  const { formatMessage } = useIntl()
  const { formatValueToDate } = useGetFormattedDate()
  const [isLoading, setIsLoading] = useState(false)
  const timestamp = getCurrentTimestamp()
  const { showErrorNotification } = useErrorNotification()

  const sanitizeFileNamePart = (value: string | undefined) => (value ?? '').replace(/[/\\:*?"<>|]/g, '_') || 'export'

  const fileName = `${sanitizeFileNamePart(requestTitle)}_${sanitizeFileNamePart(responderName)}_${timestamp}.pdf`

  const generatePDF = async () => {
    const element = elementRef.current
    if (!element) {
      sentryService.captureError(new Error('Element ref not found!'), {
        tags: {
          errorType: 'pdf-export',
          component: 'response',
          issue: 'missing-element-ref',
        },
        level: 'warning',
      })
      showErrorNotification({
        requestError: null,
        specificFallbackErrorMessage: formatMessage({ id: 'reporting.exportToPdfError' }),
      })
      return
    }

    let clone: HTMLElement | null = null

    try {
      setIsLoading(true)

      const pdfTitle = requestTitle ?? formatMessage({ id: 'reporting.response' })

      // Clone the element and position it off-screen to avoid UI flicker
      clone = element.cloneNode(true) as HTMLElement
      clone.style.position = 'fixed'
      clone.style.left = '-9999px'
      clone.style.top = '0'
      clone.style.width = `${element.offsetWidth}px`
      clone.style.backgroundColor = 'white'
      document.body.appendChild(clone)

      prepareForExport(clone, pdfTitle)

      // Allow layout to settle after modifications
      await new Promise(resolve => setTimeout(resolve, 100))

      const containerRect = clone.getBoundingClientRect()
      const scale = 2

      // Subtract a small buffer so page slices end before the next block's margin/border
      const BREAK_BUFFER_PX = 4

      const blocks = clone.querySelectorAll(PDF_BLOCK_SELECTOR)
      const breakPositionsPx: number[] = []
      blocks.forEach(block => {
        const top = block.getBoundingClientRect().top - containerRect.top - BREAK_BUFFER_PX
        if (top > 0) {
          breakPositionsPx.push(top)
        }
      })

      const fullCanvas = await html2canvas(clone, {
        scale,
        useCORS: true,
        allowTaint: true,
        logging: false,
      })

      const pdf = new jsPDF('p', 'mm', 'a4')
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()

      const contentTopMargin = styles.logo.height + styles.logo.marginBottom
      const contentWidth = pdfWidth - 2 * styles.page.padding
      const maxContentHeight = pdfHeight - contentTopMargin - styles.page.padding

      const pxToMm = contentWidth / containerRect.width
      const canvasPixelsPerPx = fullCanvas.width / containerRect.width

      const companyLabel = responderName
        ? `${formatMessage({ id: 'general.Company' }, { count: 1 })}: ${responderName}`
        : ''
      const periodLabel = periodName
        ? `${formatMessage({ id: 'reporting.reportingPeriod' }, { reportingPeriod: periodName })}`
        : ''
      const reportedDateLabel = submittedAt
        ? `${formatMessage({ id: 'reporting.reportedDate' }, { reportedDate: formatValueToDate({ value: submittedAt }) })}`
        : ''

      const logoBase64 = await imgToBase64(logo)

      const addPageHeader = () => {
        pdf.addImage(
          logoBase64,
          'PNG',
          styles.logo.marginLeft + styles.page.padding,
          styles.logo.marginTop,
          styles.logo.width,
          styles.logo.height,
          undefined,
          IMAGE_COMPRESSION,
        )

        pdf.setFont(styles.headerRow.fontFamily, styles.headerRow.fontWeight)
        pdf.setFontSize(styles.headerRow.fontSize)

        const headerRows = [companyLabel, periodLabel, reportedDateLabel].filter(Boolean)
        const maxRowWidth = Math.max(...headerRows.map(row => pdf.getTextWidth(row)))
        const startX = pdfWidth - styles.page.padding - maxRowWidth
        let headerY = styles.logo.marginTop + 1

        headerRows.forEach(row => {
          pdf.text(row, startX, headerY)
          headerY += styles.headerRow.lineHeight
        })
      }

      // Find the best break point that fits within maxHeightMm from currentPx
      const findBreakPoint = (currentPx: number, maxHeightMm: number): number => {
        const maxPx = currentPx + maxHeightMm / pxToMm
        const totalPx = containerRect.height

        if (totalPx <= maxPx) return totalPx

        // Find the last block boundary that fits
        let bestBreak: number | null = null
        for (const bp of breakPositionsPx) {
          if (bp > currentPx && bp <= maxPx) {
            bestBreak = bp
          }
        }

        // Use block boundary if it fills at least 25% of the page
        if (bestBreak !== null && (bestBreak - currentPx) * pxToMm >= maxHeightMm * 0.25) {
          return bestBreak
        }

        // Fallback: hard cut at page boundary
        return maxPx
      }

      // Slice the canvas into a sub-image
      const sliceCanvas = (topPx: number, bottomPx: number): string => {
        const sx = 0
        const sy = Math.round(topPx * canvasPixelsPerPx)
        const sw = fullCanvas.width
        const sh = Math.round((bottomPx - topPx) * canvasPixelsPerPx)

        const sliceEl = document.createElement('canvas')
        sliceEl.width = sw
        sliceEl.height = sh
        const ctx = sliceEl.getContext('2d')
        if (ctx) {
          ctx.drawImage(fullCanvas, sx, sy, sw, sh, 0, 0, sw, sh)
        }
        return sliceEl.toDataURL('image/png')
      }

      addPageHeader()
      let currentPx = 0
      let yPosition = contentTopMargin

      while (currentPx < containerRect.height) {
        const breakPx = findBreakPoint(currentPx, maxContentHeight)
        const sliceHeightPx = breakPx - currentPx
        const sliceHeightMm = sliceHeightPx * pxToMm

        if (sliceHeightPx <= 0) break

        // Start a new page if this slice won't fit
        if (yPosition + sliceHeightMm > pdfHeight - styles.page.padding && yPosition > contentTopMargin) {
          pdf.addPage()
          addPageHeader()
          yPosition = contentTopMargin + styles.page.pageBreakGap
        }

        const sliceImgData = sliceCanvas(currentPx, breakPx)
        pdf.addImage(
          sliceImgData,
          'PNG',
          styles.page.padding,
          yPosition,
          contentWidth,
          sliceHeightMm,
          undefined,
          IMAGE_COMPRESSION,
        )

        yPosition += sliceHeightMm
        currentPx = breakPx
      }

      pdf.save(fileName)
    } catch (error) {
      sentryService.captureError(toError(error, 'PDF export error'), {
        tags: {
          errorType: 'pdf-export',
          component: 'response',
          issue: 'generation-failed',
        },
        extra: {
          errorDetails: error instanceof Error ? error.message : 'Unknown error',
        },
        level: 'error',
      })
      showErrorNotification({
        requestError: null,
        specificFallbackErrorMessage: formatMessage({ id: 'reporting.exportToPdfError' }),
      })
    } finally {
      clone?.remove()
      setIsLoading(false)
    }
  }

  return { generatePDF, isLoading }
}

export default useResponseToPDF
